import { useCallback, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { api, ApiError } from './api/client'
import type { Summary, User } from './api/types'
import { Shell } from './components/Shell'
import { Loading } from './components/States'
import { ConfirmProvider } from './components/Confirm'
import { ToastProvider } from './components/Toast'
import { Contact360 } from './routes/Contact360'
import { Calendar } from './routes/Calendar'
import { Contacts } from './routes/Contacts'
import { WorkflowsHome } from './routes/WorkflowsHome'
import { DripDetail } from './routes/DripDetail'
import { DripEditor } from './routes/DripEditor'
import { Customize } from './routes/Customize'
import { Goals } from './routes/Goals'
import { MailDesigner } from './routes/MailDesigner'
import { Inbox } from './routes/Inbox'
import { LeadDetail } from './routes/LeadDetail'
import { Leads } from './routes/Leads'
import { Dialer } from './routes/Dialer'
import { LoanFile } from './routes/LoanFile'
import { Login } from './routes/Login'
import { Partners } from './routes/Partners'
import { PersonView } from './routes/PersonView'
import { Pipeline } from './routes/Pipeline'
import { Tasks } from './routes/Tasks'
import { Today } from './routes/Today'
import { WorkflowBuilder } from './routes/WorkflowBuilder'

/** Old /contacts/:key links (bookmarks, the lead platform) → the person route. */
function LegacyPerson() {
  const { personKey = '' } = useParams()
  return <Navigate to={`/partners/${encodeURIComponent(personKey)}`} replace />
}

/** New, copy-of-X and edit-Y are the same screen at different addresses. Keying
 *  it by what it's editing gives each a fresh builder — otherwise going from
 *  editing one workflow straight to "new" would carry the old steps across. */
function WorkflowBuilderRoute({ officerName }: { officerName: string }) {
  const { workflowId } = useParams()
  const [params] = useSearchParams()
  return <WorkflowBuilder key={`${workflowId ?? 'new'}|${params.get('from') ?? ''}|${params.get('draft') ?? ''}`} officerName={officerName} />
}

/** Fresh editor state per drip, same reason as the workflow builder above. */
function DripEditorRoute({ isAdmin }: { isAdmin: boolean }) {
  const { dripId } = useParams()
  return <DripEditor key={dripId} isAdmin={isAdmin} />
}

/** Rail counts go stale after writes; refresh them on every navigation. */
function SummaryOnNavigate({ onChange }: { onChange: () => void }) {
  const location = useLocation()
  useEffect(onChange, [location.pathname, onChange])
  return null
}

export default function App() {
  const [user, setUser] = useState<User | null>(null)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [booting, setBooting] = useState(true)

  // Resume an existing session on load (including one set by the lead platform on
  // the shared .bfgusa.com cookie). A 401 is the normal signed-out path.
  useEffect(() => {
    api
      .me()
      .then(setUser)
      .catch((e) => {
        if (!(e instanceof ApiError && e.isAuth)) console.error('session check failed', e)
      })
      .finally(() => setBooting(false))
  }, [])

  const loadSummary = useCallback(() => {
    if (!user) return setSummary(null)
    api.summary().then(setSummary).catch(() => {})
  }, [user])

  const logout = useCallback(() => {
    api.logout().finally(() => setUser(null))
  }, [])

  if (booting) return <Loading label="Starting…" />
  if (!user) return <ToastProvider><Login onSignedIn={setUser} /></ToastProvider>

  return (
    <ToastProvider>
    <ConfirmProvider>
      <BrowserRouter>
        <SummaryOnNavigate onChange={loadSummary} />
        <Routes>
          <Route element={<Shell user={user} summary={summary} onLogout={logout} />}>
            <Route path="/" element={<Today user={user} />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/loans/:loanId" element={<LoanFile />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/contacts/c/:contactId" element={<Contact360 user={user} />} />
            <Route path="/partners/:personKey" element={<PersonView />} />
            {/* Old links to a loan-file person still work. */}
            <Route path="/contacts/p/:personKey" element={<LegacyPerson />} />
            <Route path="/contacts/:personKey" element={<LegacyPerson />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/leads/:contactId" element={<LeadDetail />} />
            <Route path="/dialer" element={<Dialer />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/tasks" element={<Tasks user={user} />} />
            <Route path="/goals" element={<Goals />} />
            <Route path="/mail" element={<MailDesigner />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/partners" element={<Partners />} />
            <Route path="/customize" element={<Customize />} />
            <Route path="/workflows" element={<WorkflowsHome />} />
            <Route path="/drips" element={<WorkflowsHome />} />
            <Route path="/drips/new" element={<DripEditor key="new" isAdmin={user.is_admin} />} />
            <Route path="/drips/:dripId" element={<DripDetail />} />
            <Route path="/drips/:dripId/edit" element={<DripEditorRoute isAdmin={user.is_admin} />} />
            <Route path="/workflows/new" element={<WorkflowBuilderRoute officerName={user.name} />} />
            <Route path="/workflows/:workflowId/edit" element={<WorkflowBuilderRoute officerName={user.name} />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ConfirmProvider>
    </ToastProvider>
  )
}
